import { init } from '@instantdb/react'

// Instant DB 应用 ID
const APP_ID = import.meta.env.VITE_INSTANTDB_APP_ID

if (!APP_ID) {
  console.warn('⚠️ VITE_INSTANTDB_APP_ID 未配置，数据库功能不可用')
}

// 初始化 Instant DB
export const db = init({ 
  appId: APP_ID || 'placeholder-app-id' 
})

// 导出 Auth 功能（用于登录认证）
export const { useAuth } = db

// ============================================================
// 系统模板配置
// ============================================================

/**
 * 系统模板企业 ID
 * 所有在此企业下的项目将作为模板，用于初始化新企业
 */
export const SYSTEM_TEMPLATE_ORG_ID = 'system-template-org'

/**
 * 管理员企业 ID
 * 只有属于该企业的用户才能访问系统管理功能
 */
export const ADMIN_ORG_ID = '465961a6-8134-4566-a0bb-44d1225cddeb'

// ============================================================
// 批量操作工具函数
// ============================================================

/**
 * 每批最大事务数
 * 根据 InstantDB 官方建议，每批 100 个事务是安全的
 * 我们设置为 10 以确保更保守的批量处理
 */
export const BATCH_SIZE = 10

/**
 * 延迟工具函数
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * 带重试的事务执行
 * 
 * @param txn - 单个或多个事务
 * @param options - 配置选项
 * @returns Promise<void>
 * 
 * @example
 * await transactWithRetry(db.tx.notes[noteId].update({ status: 'completed' }))
 * await transactWithRetry([
 *   db.tx.notes[id1].update({ status: 'draft' }),
 *   db.tx.notes[id2].update({ status: 'draft' })
 * ])
 */
export async function transactWithRetry(
  txn: unknown | unknown[],
  options: { retries?: number; retryDelay?: number } = {}
): Promise<void> {
  const { retries = 3, retryDelay = 500 } = options
  const transactions = Array.isArray(txn) ? txn : [txn]
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await db.transact(transactions)
      return
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      
      // 如果是最后一次尝试，直接抛出错误
      if (attempt === retries) {
        console.error(`[InstantDB] 事务失败（已重试 ${retries} 次）:`, err.message)
        throw err
      }
      
      // 指数退避：等待时间递增
      const waitTime = retryDelay * Math.pow(2, attempt - 1)
      console.warn(`[InstantDB] 事务失败，${waitTime}ms 后重试 (${attempt}/${retries}):`, err.message)
      await delay(waitTime)
    }
  }
}

/**
 * 分批执行事务（解决超时问题）
 * 
 * 将大量事务分成小批次执行，每批最多 BATCH_SIZE 个
 * 每批都会自动重试，确保可靠性
 * 
 * @param transactions - 事务数组
 * @param options - 配置选项
 * @returns Promise<void>
 * 
 * @example
 * const txns = notes.map(note => db.tx.notes[note.id].update({ status: 'draft' }))
 * await batchTransact(txns) // 自动分批处理
 * 
 * @example
 * // 自定义批量大小
 * await batchTransact(txns, { batchSize: 20, retries: 5 })
 */
export async function batchTransact(
  transactions: unknown[],
  options: { batchSize?: number; retries?: number } = {}
): Promise<void> {
  const { batchSize = BATCH_SIZE, retries = 3 } = options
  
  if (transactions.length === 0) {
    return
  }
  
  // 如果事务数量小于等于批量大小，直接执行
  if (transactions.length <= batchSize) {
    await transactWithRetry(transactions, { retries })
    return
  }
  
  // 分批执行
  const totalBatches = Math.ceil(transactions.length / batchSize)
  console.log(`[InstantDB] 批量事务: 共 ${transactions.length} 条，分 ${totalBatches} 批执行`)
  
  for (let i = 0; i < transactions.length; i += batchSize) {
    const batch = transactions.slice(i, i + batchSize)
    const batchNumber = Math.floor(i / batchSize) + 1
    
    try {
      await transactWithRetry(batch, { retries })
      console.log(`[InstantDB] 批次 ${batchNumber}/${totalBatches} 完成 (${batch.length} 条)`)
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      console.error(`[InstantDB] 批次 ${batchNumber}/${totalBatches} 失败:`, err.message)
      throw new Error(`批量事务失败: 批次 ${batchNumber}/${totalBatches} - ${err.message}`)
    }
  }
  
  console.log(`[InstantDB] 批量事务完成: ${transactions.length} 条记录已处理`)
}

